import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Typography,
  Chip,
  TextField,
  Button,
  Collapse,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { memo, useState } from 'react';
import type { GroupMemberWithProfile } from '../hooks/useGroupMembers';

export type OrganizerSelectorProps = {
  readonly uid: string;
  readonly organizerName: string;
  readonly members: GroupMemberWithProfile[];
  readonly onSelect: (uid: string, displayName: string) => void;
  readonly error?: string;
  readonly disabled?: boolean;
  readonly loading?: boolean;
};

/**
 * Component for selecting a group organizer from the list of group members
 * Shows member names in dropdown, uses UIDs as values
 * Includes manual UID entry option for advanced users
 */
const OrganizerSelector = memo<OrganizerSelectorProps>(
  ({
    uid,
    organizerName,
    members,
    onSelect,
    error,
    disabled = false,
    loading = false,
  }) => {
    const [showManualInput, setShowManualInput] = useState(false);
    const [manualUid, setManualUid] = useState('');

    // Only show active members in the dropdown
    const activeMembers = members.filter(m => m.status === 'active');

    // Check if current UID is in the members list
    const currentUidInList = activeMembers.some(m => m.uid === uid);

    // Use the UID value only if it's in the list, otherwise use empty string to avoid MUI warning
    const selectValue = currentUidInList ? uid : '';

    const handleSelectChange = (selectedUid: string) => {
      const selectedMember = activeMembers.find(m => m.uid === selectedUid);
      if (selectedMember) {
        onSelect(selectedUid, selectedMember.profile.displayName);
      }
    };

    const handleManualSubmit = () => {
      if (manualUid.trim()) {
        onSelect(manualUid.trim(), ''); // Name will be resolved by OrganizerPicker logic
        setShowManualInput(false);
        setManualUid('');
      }
    };

    return (
      <Box>
        <FormControl fullWidth error={!!error} disabled={disabled || loading}>
          <InputLabel id="organizer-select-label">Group Organizer *</InputLabel>
          <Select
            labelId="organizer-select-label"
            value={selectValue}
            onChange={e => handleSelectChange(e.target.value)}
            label="Group Organizer *"
          >
            {activeMembers.map(member => (
              <MenuItem key={member.uid} value={member.uid}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
                >
                  <PersonIcon fontSize="small" color="action" />
                  <Typography>{member.profile.displayName}</Typography>
                  {member.uid === uid && (
                    <Chip
                      label="Current"
                      size="small"
                      color="primary"
                      sx={{ ml: 'auto' }}
                    />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
          {error && <FormHelperText>{error}</FormHelperText>}
          {!error && (
            <FormHelperText>
              Select a group member to transfer organizer role
            </FormHelperText>
          )}
        </FormControl>

        {/* Current organizer display */}
        {uid && organizerName && (
          <Box
            sx={{
              mt: 1.5,
              ml: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <PersonIcon fontSize="small" color="primary" />
            <Typography variant="body2" color="text.primary">
              <strong>Current Organizer:</strong> {organizerName}
            </Typography>
          </Box>
        )}

        {/* Manual UID entry option */}
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            onClick={() => setShowManualInput(!showManualInput)}
            endIcon={showManualInput ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: 'none' }}
          >
            {showManualInput ? 'Hide' : 'Show'} manual UID entry
          </Button>

          <Collapse in={showManualInput}>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Enter a user ID to transfer ownership to someone who isn't a member yet
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="User ID"
                  value={manualUid}
                  onChange={e => setManualUid(e.target.value)}
                  placeholder="Enter UID manually"
                />
                <Button
                  variant="contained"
                  onClick={handleManualSubmit}
                  disabled={!manualUid.trim()}
                >
                  Set
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Box>
    );
  },
);

OrganizerSelector.displayName = 'OrganizerSelector';

export default OrganizerSelector;
